/**
 * `landing` namespace — English. This is the source of truth: `lo.ts` mirrors
 * its key tree exactly, enforced at compile time by `TranslationShape<typeof en>`.
 *
 * All page copy lives here. Components render `t(...)` and hold no literal text,
 * so adding a language never means touching a component.
 *
 * Copy transcribed from design/english.png; the Lao original is design/code.html.
 */
export const en = {
  meta: {
    title: 'CHALEUN XAYMOUNGKHOUN Co., Ltd — Trading, Energy, Real Estate & Mining',
    description:
      'A Lao multi-service conglomerate building the future through integrated industrial strength, sustainable energy, and global trade excellence.',
  },

  brand: {
    name: 'CHALEUN XAYMOUNGKHOUN',
    suffix: 'Co., Ltd',
  },

  nav: {
    home: 'Home',
    services: 'Services',
    about: 'About',
    contact: 'Contact',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    primaryLabel: 'Primary',
  },

  language: {
    label: 'Language',
    switchTo: 'Switch to {{language}}',
  },

  hero: {
    title: 'CHALEUN XAYMOUNGKHOUN Co., Ltd',
    subtitle:
      'Building the future through integrated industrial strength, sustainable energy, and global trade excellence.',
    ctaPrimary: 'Explore Our Services',
    ctaSecondary: 'Contact Us',
    carouselLabel: 'Featured operations',
    previousSlide: 'Previous image',
    nextSlide: 'Next image',
    goToSlide: 'Show image {{number}} of {{total}}',
    slides: {
      headquarters: 'The corporate headquarters at dusk, seen across the river',
      energy: 'A solar and wind generation facility at sunset',
      logistics: 'An aerial view of a large-scale logistics and distribution hub',
    },
  },

  about: {
    eyebrow: 'Company Profile',
    heading: 'Institutional Trust & Industrial Strength',
    body1:
      'CHALEUN XAYMOUNGKHOUN Co., Ltd. stands as a pillar of reliability in the region. We operate a multi-service conglomerate dedicated to driving economic growth while maintaining the highest standards of structural integrity and operational excellence.',
    body2:
      'Our vision is to seamlessly integrate large-scale corporate operations with precise engineering across our core sectors: Trading, Energy, Land & House development, and Mining. We build lasting partnerships founded on transparency, professional excellence, and a commitment to sustainable development.',
    link: 'Learn more about our vision',
    imageAlt: 'A modern renewable energy facility with solar arrays and wind turbines',
  },

  sectors: {
    eyebrow: 'Our Core Sectors',
    heading: 'Integrated Solutions',
    trading: {
      title: 'Trading',
      description:
        'Global commerce and robust supply chain management, ensuring the seamless flow of goods and materials across borders.',
    },
    energy: {
      title: 'Energy',
      description:
        'Delivering sustainable and traditional power solutions to fuel industrial growth and community development.',
    },
    realEstate: {
      title: 'Real Estate',
      description:
        'Premium real estate development and property management, building structural foundations for success.',
    },
    mining: {
      title: 'Mining',
      description:
        'Responsible and efficient resource extraction, utilizing advanced heavy machinery and safety standards.',
    },
  },

  contact: {
    heading: 'Corporate Headquarters',
    intro: 'Connect with our team to discuss partnerships, services, and industrial opportunities.',
    addressLabel: 'Address',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    websiteLabel: 'Website',
    addressLine1: 'Ban Nonvay, Xaysettha District',
    addressLine2: 'Vientiane Capital, Laos PDR',
    mapTitle: 'Map showing the location of our head office',
  },

  footer: {
    copyright: '© {{year}} CHALEUN XAYMOUNGKHOUN Co., Ltd. All Rights Reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    legal: 'Legal Notice',
    sustainability: 'Sustainability',
  },
} as const

export type LandingBundle = typeof en
